import { STATUS_CONFIG, scoreColor } from '@workspace/seedforge-shared'
import { cn } from '@workspace/ui/lib/utils'
import { ArrowRight } from 'lucide-react'
import { Prose } from '../../components/prose'
import type { EvalCategory } from '../types'
import { FindingRow } from './finding-row'

export { CategoryCard as EvaluationCategoryCard }

export function CategoryCard({
  category,
  className,
}: {
  category: EvalCategory
  className?: string
}) {
  const config = STATUS_CONFIG[category.status]

  return (
    <div
      id={`category-${category.key}`}
      className={cn(
        'flex scroll-mt-6 flex-col rounded-xl border border-border/60 bg-zinc-900/60 transition-colors hover:border-border',
        className
      )}
    >
      {/* Header + Summary */}
      <div className="px-5 pt-[18px] pb-3.5">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{category.icon}</span>
            <h3 className="text-[15px] font-semibold text-foreground">{category.label}</h3>
          </div>
          <div className="flex items-center gap-2">
            {category.score != null && (
              <span
                className={cn('text-sm font-semibold tabular-nums', scoreColor(category.score))}
              >
                {category.score}
              </span>
            )}
            <span
              className={cn(
                'inline-flex items-center rounded border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                config.bg,
                config.color,
                config.border
              )}
            >
              {config.label}
            </span>
          </div>
        </div>
        <Prose className="text-xs leading-relaxed text-muted-foreground/60">
          {category.summary}
        </Prose>
      </div>

      {/* Findings — contained rows */}
      <div className="space-y-1.5 px-3 pb-2.5">
        {category.findings.map((f) => (
          <FindingRow key={f.text} finding={f} />
        ))}
      </div>

      {/* Next Step — prominent orange box */}
      {category.nextStep && (
        <div className="mx-3 mb-3 rounded-lg border border-[#f97316]/25 bg-[#f97316]/[0.08] px-3 py-2.5">
          <div className="flex items-start gap-2">
            <ArrowRight className="mt-0.5 size-3.5 shrink-0 text-[#f97316]" />
            <div>
              <p className="text-xs font-semibold text-[#f97316]">{category.nextStep.title}</p>
              <Prose className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground/60">
                {category.nextStep.detail}
              </Prose>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
